import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Cursor, PencilSimpleLine, Scissors, CircleDashed, Eye, EyeSlash, ArrowCounterClockwise, Keyboard, Sparkle, DeviceTablet, Camera, X, MagicWand, Lightning } from "@phosphor-icons/react";
import { money } from "@/lib/format";
import { API } from "@/lib/api";
import { detectCorners, traceOutline } from "./cornerDetect";
import { normalizePiece, splitPiece, cornerCut, removeCorner, cornerFeatures, CUTOUT_SIZE, SHAPES } from "@/components/CountertopDesigner";
import { SketchCanvas } from "./SketchCanvas";
import { SketchPanel, bumpMostUsed } from "./SketchPanel";
import { NumPad } from "./NumPad";
import { LETTER, absPts, constrain, distToSeg, dropCollinear, finishedLf, fromAbs, insidePoly, pieceSqft, r2, setSideLength, snapTo, uid } from "./sketchGeom";

const TOOLS = [
  ["select", Cursor, "Select", "V"],
  ["draw", PencilSimpleLine, "Draw", "D"],
  ["seam", Scissors, "Seam", "S"],
  ["cutout", CircleDashed, "Cut-out", "C"],
];

export const SketchMode = ({
  pieces = [],
  onChange,
  edgeProfiles = [],
  cutoutKinds = [],
  products = [],
  services = [],
  areas = [],
  currentAreaId = "",
  onMovePiece,
  onSelectArea,
  areaName = "",
  liveTotal,
  onExit,
  trace = null,
  onTraceChange,
  onUploadTrace,
}) => {
  const list = useMemo(() => pieces.map(normalizePiece), [pieces]);
  const [tool, setToolRaw] = useState("draw");
  const [ortho, setOrtho] = useState(90);
  const [snap, setSnap] = useState(1);
  const [sel, setSel] = useState(null);
  const [hover, setHover] = useState(null);
  const [draw, setDraw] = useState({ pts: [], cursor: null, typed: "" });
  const [seamPreview, setSeamPreview] = useState(null);
  const [cutoutKind, setCutoutKind] = useState("Undermount sink");
  const [showDims, setShowDims] = useState(true);
  const [view, setView] = useState({ x: -6, y: -6, w: 220, h: 130 });
  const [dimEdit, setDimEdit] = useState(null); // {pieceId, edge, left, top, value}
  const [touch, setTouch] = useState(() => {
    try {
      return window.matchMedia("(pointer: coarse)").matches || localStorage.getItem("sketch2_touch") === "1";
    } catch (e) {
      return false;
    }
  });
  const toggleTouch = () => {
    setTouch((t) => {
      try {
        localStorage.setItem("sketch2_touch", t ? "0" : "1");
      } catch (e) {
        /* ignore */
      }
      return !t;
    });
  };
  const dragRef = useRef(null);
  const wrapRef = useRef(null);
  const histRef = useRef([]);
  const fileRef = useRef(null);
  const [traceMove, setTraceMove] = useState(false);
  const [calib, setCalib] = useState(null); // {pts:[[x,y],[x,y]], value:""}
  const [suggest, setSuggest] = useState(null); // detected corners in sheet inches
  const [detecting, setDetecting] = useState(false);
  const detect = async () => {
    if (!trace || !traceUrl) return;
    if (suggest) return setSuggest(null);
    setDetecting(true);
    try {
      const pts = await detectCorners(traceUrl, trace);
      setSuggest(pts);
      if (!pts.length) toast.error("No clear corners found — try a sharper, higher-contrast photo");
      else {
        toast.success(`${pts.length} corner${pts.length > 1 ? "s" : ""} suggested — tap each one to trace`);
        setTool("draw");
      }
    } catch (e) {
      toast.error("Could not read the photo");
    } finally {
      setDetecting(false);
    }
  };
  const autoTrace = async () => {
    if (!trace || !traceUrl) return;
    setDetecting(true);
    try {
      const corners = suggest || (await detectCorners(traceUrl, trace));
      if (!suggest) setSuggest(corners);
      const ring = await traceOutline(traceUrl, trace, corners, { ortho: ortho === 90 });
      if (!ring) return toast.error("Couldn't find a closed outline — check the photo shows the whole shape");
      if (addPolygon(ring)) {
        setToolRaw("select");
        setDraw({ pts: [], cursor: null, typed: "" });
        toast.success(`Traced a ${ring.length}-sided piece — tap any side to type its exact length`);
      }
    } catch (e) {
      toast.error("Could not read the photo");
    } finally {
      setDetecting(false);
    }
  };
  // Re-map suggestions when the photo is moved / scaled.
  const traceGeomRef = useRef(null);
  useEffect(() => {
    if (!trace) return setSuggest(null);
    const prev = traceGeomRef.current;
    traceGeomRef.current = { x: trace.x || 0, y: trace.y || 0, w: trace.w_in || 120 };
    if (!prev || !suggest) return;
    const f = traceGeomRef.current.w / prev.w;
    setSuggest(suggest.map(([x, y]) => [r2(traceGeomRef.current.x + (x - prev.x) * f), r2(traceGeomRef.current.y + (y - prev.y) * f)]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trace?.x, trace?.y, trace?.w_in, trace?.path]);
  const snapSuggest = (pt) => {
    if (!suggest?.length) return null;
    const tol = Math.max(1.5, view.w * 0.02);
    let best = null;
    for (const s of suggest) {
      const d = Math.hypot(s[0] - pt[0], s[1] - pt[1]);
      if (d < tol && (!best || d < best.d)) best = { d, pt: s };
    }
    return best?.pt || null;
  };
  const applyCalib = (val) => {
    const real = Number(val);
    if (!trace || !calib || calib.pts.length < 2 || !(real > 0)) return setCalib(null);
    const [a, b] = calib.pts;
    const measured = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (measured < 0.1) return setCalib(null);
    const f = real / measured;
    const w = r2((trace.w_in || 120) * f);
    onTraceChange?.({ ...trace, w_in: w, x: r2(a[0] - (a[0] - (trace.x || 0)) * f), y: r2(a[1] - (a[1] - (trace.y || 0)) * f) });
    setCalib(null);
    toast.success(`Photo scaled — now ${Math.round(w)}" wide`);
  };
  const traceUrl = trace?.path ? `${API}/photos/${trace.path}?auth=${localStorage.getItem("sw_token")}` : "";
  const uploadTrace = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !onUploadTrace) return;
    const aspect = await new Promise((res) => {
      const img = new Image();
      img.onload = () => res(img.naturalWidth / (img.naturalHeight || 1));
      img.onerror = () => res(1.5);
      img.src = URL.createObjectURL(file);
    });
    const path = await onUploadTrace(file);
    if (!path) return;
    onTraceChange?.({ path, aspect: r2(aspect || 1.5), w_in: Math.round(view.w * 0.7), x: r2(view.x + view.w * 0.15), y: r2(view.y + view.h * 0.1), opacity: 0.55 });
    setTraceMove(true);
    toast.success("Photo placed — drag it into position, set its real width, then trace the corners");
  };

  const push = useCallback(
    (next) => {
      histRef.current = [...histRef.current.slice(-40), list];
      onChange(next);
    },
    [list, onChange],
  );
  const undo = () => {
    const prev = histRef.current.pop();
    if (prev) onChange(prev);
  };
  const update = (id, patch) => push(list.map((p) => (p.id === id ? normalizePiece({ ...p, ...patch }) : p)));
  const replace = (id, piece) => push(list.map((p) => (p.id === id ? piece : p)));
  const selPiece = list.find((p) => p.id === sel?.pieceId) || null;

  const setTool = (t) => {
    setToolRaw(t);
    setDraw(t === "draw" ? { pts: [], cursor: null, typed: "" } : null);
    setSeamPreview(null);
    if (t !== "select") setSel(null);
  };

  // keep the view roughly fitted to the wrapper aspect
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const { width, height } = el.getBoundingClientRect();
      if (width && height) setView((v) => ({ ...v, h: r2((v.w * height) / width) }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const freeSpot = () => {
    const maxY = list.reduce((m, p) => Math.max(m, p.y + p.width_in), 0);
    return [4, list.length ? maxY + 8 : 4];
  };

  const addPolygon = (ring) => {
    ring = dropCollinear(ring);
    if (ring.length < 3) {
      toast.error("Need at least three corners");
      return false;
    }
    const minx = Math.min(...ring.map((p) => p[0]));
    const miny = Math.min(...ring.map((p) => p[1]));
    const piece = normalizePiece({
      id: uid(),
      label: `Piece ${list.length + 1}`,
      points: ring.map(([x, y]) => [r2(x - minx), r2(y - miny)]),
      x: r2(minx),
      y: r2(miny),
      rot: 0,
      backsplash_sqft: 0,
    });
    push([...list, piece]);
    setSel({ pieceId: piece.id });
    return true;
  };
  const addRect = (w, h, at) => {
    const [x, y] = at || freeSpot();
    const piece = normalizePiece({ id: uid(), label: `Piece ${list.length + 1}`, points: SHAPES.Rectangle(w, h), x: r2(x), y: r2(y), rot: 0, backsplash_sqft: 0 });
    push([...list, piece]);
    setSel({ pieceId: piece.id });
    toast.success(`${w}" × ${h}" piece added`);
  };

  const snapPt = ([x, y], from) => {
    let p = [snapTo(x, snap), snapTo(y, snap)];
    if (from) p = constrain(from, p, ortho).map((v) => snapTo(v, snap));
    return p;
  };
  const closeTol = Math.max(1.5, snap * 1.5);

  const finishDraw = () => {
    if (!draw) return;
    let pts = draw.pts;
    if (pts.length > 3 && Math.hypot(pts[0][0] - pts[pts.length - 1][0], pts[0][1] - pts[pts.length - 1][1]) < closeTol) pts = pts.slice(0, -1);
    if (addPolygon(pts)) setDraw({ pts: [], cursor: null, typed: "" });
  };

  // ----- typed lengths: "96 Enter" extends toward the cursor; "96 Tab 25.5 Enter" = rectangle
  const commitTyped = () => {
    const n = Number(draw?.typed);
    if (!draw || !(n > 0)) return false;
    if (draw.rectW != null) {
      addRect(draw.rectW, n, draw.pts[0]);
      setDraw({ pts: [], cursor: null, typed: "" });
      return true;
    }
    if (!draw.pts.length) {
      const at = freeSpot();
      setDraw({ ...draw, pts: [at, [r2(at[0] + n), at[1]]], typed: "", cursor: null });
      return true;
    }
    const last = draw.pts[draw.pts.length - 1];
    let dir;
    if (draw.cursor) {
      const c = constrain(last, draw.cursor, ortho || 90);
      const L = Math.hypot(c[0] - last[0], c[1] - last[1]) || 1;
      dir = [(c[0] - last[0]) / L, (c[1] - last[1]) / L];
    } else if (draw.pts.length >= 2) {
      const prev = draw.pts[draw.pts.length - 2];
      const L = Math.hypot(last[0] - prev[0], last[1] - prev[1]) || 1;
      dir = [(last[1] - prev[1]) / L, -(last[0] - prev[0]) / L]; // turn clockwise
    } else dir = [1, 0];
    const next = [r2(last[0] + dir[0] * n), r2(last[1] + dir[1] * n)];
    const first = draw.pts[0];
    if (draw.pts.length >= 3 && Math.hypot(first[0] - next[0], first[1] - next[1]) < closeTol) {
      addPolygon(draw.pts);
      setDraw({ pts: [], cursor: null, typed: "" });
      return true;
    }
    setDraw({ ...draw, pts: [...draw.pts, next], typed: "" });
    return true;
  };

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target.isContentEditable) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        undo();
        return;
      }
      if (e.key === "Escape") {
        if (draw?.pts?.length) setDraw({ pts: [], cursor: null, typed: "" });
        else if (tool !== "select") setTool("select");
        else setSel(null);
        setDimEdit(null);
        return;
      }
      if (tool === "draw" && draw) {
        if (/^[0-9.]$/.test(e.key)) {
          setDraw({ ...draw, typed: (draw.typed || "") + e.key });
          e.preventDefault();
          return;
        }
        if (e.key === "Backspace") {
          if (draw.typed) setDraw({ ...draw, typed: draw.typed.slice(0, -1) });
          else if (draw.pts.length) setDraw({ ...draw, pts: draw.pts.slice(0, -1) });
          e.preventDefault();
          return;
        }
        if (e.key === "Tab") {
          const n = Number(draw.typed);
          if (n > 0) setDraw({ ...draw, rectW: n, typed: "", pts: draw.pts.slice(0, 1) });
          e.preventDefault();
          return;
        }
        if (e.key === "Enter") {
          e.preventDefault();
          if (!commitTyped()) finishDraw();
          return;
        }
      }
      if ((e.key === "Delete" || e.key === "Backspace") && selPiece) {
        e.preventDefault();
        if (sel.cutout) update(selPiece.id, { cutouts: selPiece.cutouts.filter((c) => c.id !== sel.cutout) });
        else {
          push(list.filter((p) => p.id !== selPiece.id));
          setSel(null);
        }
        return;
      }
      const k = e.key.toUpperCase();
      const t = TOOLS.find(([, , , key]) => key === k);
      if (t && !e.ctrlKey && !e.metaKey) setTool(t[0]);
      if (k === "O") setOrtho((o) => (o === 90 ? 45 : o === 45 ? 0 : 90));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // ----- pointer handling on the empty canvas
  const onPointer = (type, pt, e) => {
    if (calib && trace) {
      if (type === "move") return setCalib({ ...calib, cursor: pt });
      if (type === "down" && e.button === 0 && calib.pts.length < 2) {
        const pts = [...calib.pts, pt];
        setCalib({ ...calib, pts, cursor: pt });
        if (pts.length === 2) toast("Now type the real distance between those two points");
      }
      return;
    }
    if (traceMove && trace && type === "down" && e.button === 0) {
      dragRef.current = { kind: "trace", start: pt, tx: trace.x || 0, ty: trace.y || 0 };
      return;
    }
    if (type === "move" && dragRef.current?.kind === "trace") {
      const d = dragRef.current;
      onTraceChange?.({ ...trace, x: r2(d.tx + pt[0] - d.start[0]), y: r2(d.ty + pt[1] - d.start[1]) });
      return;
    }
    if (type === "down" && (e.button === 1 || e.shiftKey)) {
      dragRef.current = { kind: "pan", start: pt, view };
      return;
    }
    if (type === "move" && dragRef.current?.kind === "pan") {
      const d = dragRef.current;
      setView({ ...d.view, x: r2(d.view.x - (pt[0] - d.start[0])), y: r2(d.view.y - (pt[1] - d.start[1])) });
      return;
    }
    if (type === "move" && dragRef.current?.kind === "piece") {
      const d = dragRef.current;
      const nx = snapTo(d.px + pt[0] - d.start[0], snap);
      const ny = snapTo(d.py + pt[1] - d.start[1], snap);
      if (nx !== d.last?.[0] || ny !== d.last?.[1]) {
        d.last = [nx, ny];
        onChange(list.map((p) => (p.id === d.id ? { ...p, x: nx, y: ny } : p)));
      }
      return;
    }
    if (type === "move" && dragRef.current?.kind === "cutout") {
      const d = dragRef.current;
      const piece = list.find((p) => p.id === d.pieceId);
      if (!piece) return;
      const nx = snapTo(d.cx + pt[0] - d.start[0], 0.25);
      const ny = snapTo(d.cy + pt[1] - d.start[1], 0.25);
      onChange(list.map((p) => (p.id === d.pieceId ? { ...p, cutouts: p.cutouts.map((c) => (c.id === d.id ? { ...c, x: nx, y: ny } : c)) } : p)));
      return;
    }
    if (type === "up") {
      if (dragRef.current && dragRef.current.kind !== "pan" && dragRef.current.moved) histRef.current.push(dragRef.current.before);
      dragRef.current = null;
      return;
    }
    if (tool === "draw" && draw) {
      const from = draw.pts[draw.pts.length - 1];
      const hit = snapSuggest(pt);
      const p = hit || snapPt(pt, from);
      if (type === "move") {
        setDraw({ ...draw, cursor: p, onSuggest: !!hit });
        return;
      }
      if (type === "down" && e.button === 0) {
        if (draw.rectW != null) {
          addRect(draw.rectW, Math.max(1, Math.abs(p[1] - (draw.pts[0]?.[1] ?? p[1])) || 25.5), draw.pts[0] || p);
          setDraw({ pts: [], cursor: null, typed: "" });
          return;
        }
        if (draw.pts.length >= 3 && Math.hypot(p[0] - draw.pts[0][0], p[1] - draw.pts[0][1]) < closeTol) {
          finishDraw();
          return;
        }
        setDraw({ ...draw, pts: [...draw.pts, p] });
        return;
      }
      if (type === "dbl") finishDraw();
      return;
    }
    if (tool === "seam") {
      const piece = list.find((p) => insidePoly(pt, absPts(p)));
      if (type === "move") {
        if (!piece) return setSeamPreview(null);
        const A = absPts(piece);
        const cx = piece.x + piece.length_in / 2;
        const vertical = Math.abs(pt[0] - cx) < piece.length_in * 0.4 || piece.length_in > piece.width_in;
        setSeamPreview({ pieceId: piece.id, axis: vertical ? 0 : 1, at: snapTo(pt[vertical ? 0 : 1], snap), A });
        return;
      }
      if (type === "down" && piece && seamPreview) {
        const at = seamPreview.at - (seamPreview.axis === 0 ? piece.x : piece.y);
        const parts = splitPiece(piece, seamPreview.axis, at);
        if (!parts) return toast.error("Seam must cross the piece");
        push(list.flatMap((p) => (p.id === piece.id ? parts.map((q, i) => ({ ...q, label: i ? `${piece.label} B` : `${piece.label} A` })) : [p])));
        toast.success("Seam added — two pieces");
        setSeamPreview(null);
      }
      return;
    }
    if (tool === "cutout") {
      if (type === "move") return setHover({ at: pt, kind: cutoutKind });
      if (type === "down") {
        const piece = list.find((p) => insidePoly(pt, absPts(p)));
        if (!piece) return toast.error("Click inside a piece");
        placeCutout(piece, cutoutKind, pt);
      }
      return;
    }
    if (type === "down" && e.button === 0) {
      setSel(null);
      setDimEdit(null);
    }
    if (type === "move") setHover(null);
  };

  const placeCutout = (piece, kind, at, extra = {}) => {
    const [w, h] = CUTOUT_SIZE[kind] || [24, 18];
    const round = ["Faucet hole", "Soap dispenser"].includes(kind);
    const x = Math.max(0, Math.min(piece.length_in - w, snapTo(at[0] - piece.x - w / 2, 0.5)));
    const y = Math.max(0, Math.min(piece.width_in - h, snapTo(at[1] - piece.y - h / 2, 0.5)));
    const c = {
      id: uid(),
      kind,
      w,
      h,
      x: r2(x),
      y: r2(y),
      rot: 0,
      shape: round ? "Round" : "Rectangle",
      sink_type: kind === "Farmhouse sink" ? "Farmhouse" : "Undermount",
      faucet_holes: /sink/i.test(kind) ? 1 : 0,
      ...extra,
    };
    update(piece.id, { cutouts: [...(piece.cutouts || []), c] });
    bumpMostUsed(`cutout:${kind}`);
    setToolRaw("select");
    setHover(null);
    setSel({ pieceId: piece.id, cutout: c.id });
  };

  const onPieceClick = (piece, pt, e) => {
    if (tool === "draw" || tool === "seam") return onPointer("down", pt, e);
    if (tool === "cutout") return placeCutout(piece, cutoutKind, pt);
    setSel({ pieceId: piece.id });
    setDimEdit(null);
    dragRef.current = { kind: "piece", id: piece.id, start: pt, px: piece.x, py: piece.y, before: list, moved: true };
  };
  const onCutoutPointer = (piece, c, pt) => {
    if (tool !== "select") return;
    setSel({ pieceId: piece.id, cutout: c.id });
    dragRef.current = { kind: "cutout", pieceId: piece.id, id: c.id, start: pt, cx: c.x, cy: c.y, before: list, moved: true };
  };
  const onEdgeClick = (piece, i) => {
    if (tool !== "select") return;
    setSel({ pieceId: piece.id, edge: i });
    setDimEdit(null);
  };
  const onCornerClick = (piece, i) => setSel({ pieceId: piece.id, corner: i });
  const onDimClick = (piece, i, at) => {
    setSel({ pieceId: piece.id, edge: i });
    const box = wrapRef.current?.getBoundingClientRect();
    if (!box) return;
    setDimEdit({ pieceId: piece.id, edge: i, left: ((at[0] - view.x) / view.w) * 100, top: ((at[1] - view.y) / view.h) * 100, value: "" });
  };

  // On-screen number pad (tablet): feeds the dimension editor when open, else the draw tool.
  const padKey = (k) => {
    if (calib?.pts?.length === 2) {
      if (k === "⌫") return setCalib({ ...calib, value: (calib.value || "").slice(0, -1) });
      if (k === "Esc") return setCalib(null);
      if (k === "Enter") return applyCalib(calib.value);
      if (k === "W×D") return;
      return setCalib({ ...calib, value: (calib.value || "") + k });
    }
    if (dimEdit) {
      if (k === "⌫") return setDimEdit({ ...dimEdit, value: (dimEdit.value || "").slice(0, -1) });
      if (k === "Esc") return setDimEdit(null);
      if (k === "Enter") return commitDim(dimEdit.value);
      if (k === "W×D") return;
      return setDimEdit({ ...dimEdit, value: (dimEdit.value || "") + k });
    }
    if (tool !== "draw") setTool("draw");
    const d = draw || { pts: [], cursor: null, typed: "" };
    if (k === "⌫") return setDraw(d.typed ? { ...d, typed: d.typed.slice(0, -1) } : { ...d, pts: d.pts.slice(0, -1) });
    if (k === "Esc") return setDraw({ pts: [], cursor: null, typed: "" });
    if (k === "W×D") {
      const n = Number(d.typed);
      return n > 0 && setDraw({ ...d, rectW: n, typed: "", pts: d.pts.slice(0, 1) });
    }
    if (k === "Enter") return commitTyped() || finishDraw();
    setDraw({ ...d, typed: (d.typed || "") + k });
  };
  const commitDim = (val) => {
    const piece = list.find((p) => p.id === dimEdit?.pieceId);
    const L = Number(val);
    if (piece && L > 0 && Math.abs(L - (piece.edges?.[dimEdit.edge]?.length_in || 0)) > 0.001) {
      if (piece.edges?.[dimEdit.edge]?.locked) toast.error("That side is locked — unlock it first");
      else {
        const next = setSideLength(piece, dimEdit.edge, L);
        if (!next) {
          const n = piece.points.length;
          const blockers = [(dimEdit.edge + 2) % n, (dimEdit.edge - 2 + n) % n].filter((k) => piece.edges?.[k]?.locked).map((k) => LETTER(k));
          toast.error(`Side ${blockers.join(" and ")} is locked and would have to change — unlock it to resize`);
        } else {
          replace(piece.id, next);
          toast.success(`Side set to ${L}"`);
        }
      }
    }
    setDimEdit(null);
  };

  const setEdge = (patch) => {
    if (!selPiece || sel.edge == null) return;
    update(selPiece.id, { edges: selPiece.edges.map((e, i) => (i === sel.edge ? { ...e, ...patch } : e)) });
    if (patch.profile) bumpMostUsed(`profile:${patch.profile}`);
  };
  const setEdgeAll = (patch) =>
    selPiece && update(selPiece.id, { edges: selPiece.edges.map((e) => (e.profile !== "Unfinished" && !e.seam ? { ...e, ...patch } : e)) });
  const cornerFeatureAt = (piece, v) => {
    if (!piece || v == null) return null;
    const n = piece.points.length;
    return cornerFeatures(piece).find((f) => f.edges.includes(v) || f.edges.includes((v - 1 + n) % n)) || null;
  };
  const selCornerFeature = cornerFeatureAt(selPiece, sel?.corner);
  const applyCorner = (kind, size) => {
    if (!selPiece || sel.corner == null) return;
    const existing = selCornerFeature;
    let base = selPiece;
    let v = sel.corner;
    if (existing) {
      const r = removeCorner(selPiece, existing.id);
      base = r.piece;
      v = r.vertex;
    }
    const next = cornerCut(base, v, Math.max(0.25, size), kind, { id: uid() });
    replace(selPiece.id, next);
    setSel({ pieceId: selPiece.id, corner: v });
  };
  const setCut = (patch) =>
    selPiece && update(selPiece.id, { cutouts: selPiece.cutouts.map((c) => (c.id === sel.cutout ? { ...c, ...patch } : c)) });
  const addProduct = (p) => {
    if (!selPiece) return toast.error("Select a piece first");
    const kind = /farm/i.test(p.label) ? "Farmhouse sink" : /drop/i.test(p.label) ? "Drop-in sink" : "Undermount sink";
    const cx = selPiece.x + selPiece.length_in / 2;
    const cy = selPiece.y + Math.min(selPiece.width_in - 2, 3 + (CUTOUT_SIZE[kind]?.[1] || 18) / 2);
    placeCutout(selPiece, kind, [cx, cy], { product: p.label });
    bumpMostUsed(`product:${p.label}`);
  };
  const applyService = (s) => {
    if (!selPiece) return;
    const meta = { service_kind: "service", service_id: s.id, service_name: s.name, service_price: Number(s.price) || 0, service_unit: s.unit || "EA" };
    if (sel.cutout) setCut(meta);
    else if (sel.corner != null) {
      const rec = selCornerFeature;
      if (!rec) return toast.error("Give the corner a radius, clip or notch first");
      update(selPiece.id, { corners: (selPiece.corners || []).map((c) => (c.id === rec.id ? { ...c, ...meta } : c)) });
    }
    bumpMostUsed(`service:${s.id}`);
    toast.success(`${s.name} added`);
  };

  const zoom = (f, at) => {
    setView((v) => {
      const w = Math.max(40, Math.min(600, v.w * f));
      const h = (v.h * w) / v.w;
      const cx = at ? at[0] : v.x + v.w / 2;
      const cy = at ? at[1] : v.y + v.h / 2;
      return { x: r2(cx - (cx - v.x) * (w / v.w)), y: r2(cy - (cy - v.y) * (h / v.h)), w: r2(w), h: r2(h) };
    });
  };
  const fit = () => {
    if (!list.length) return setView({ x: -6, y: -6, w: 220, h: 130 });
    const xs = list.flatMap((p) => [p.x, p.x + p.length_in]);
    const ys = list.flatMap((p) => [p.y, p.y + p.width_in]);
    const pad = 14;
    const w = Math.max(...xs) - Math.min(...xs) + pad * 2;
    const box = wrapRef.current?.getBoundingClientRect();
    const aspect = box ? box.height / box.width : 0.6;
    const h = Math.max(w * aspect, Math.max(...ys) - Math.min(...ys) + pad * 2);
    setView({ x: r2(Math.min(...xs) - pad), y: r2(Math.min(...ys) - pad), w: r2(Math.max(w, h / aspect)), h: r2(Math.max(h, w * aspect)) });
  };

  const totals = {
    pieces: list.length,
    sqft: list.reduce((s, p) => s + pieceSqft(p), 0),
    lf: list.reduce((s, p) => s + finishedLf(p), 0),
    cutouts: list.reduce((s, p) => s + (p.cutouts || []).length, 0),
  };
  const hint = {
    draw: draw?.rectW != null ? `Width ${draw.rectW}" set — type the depth and press Enter` : draw?.pts?.length ? "Click the next corner · type a length + Enter · click the first corner or Enter to close · Esc to cancel" : "Click the first corner on the grid, or type a length (96 Tab 25.5 Enter = rectangle)",
    select: "Click a side to set its edge · click a dimension to type it · drag pieces and cut-outs · Del removes",
    seam: "Move over a piece and click where the seam goes (snaps to the grid)",
    cutout: `Click on a piece to drop a ${cutoutKind}`,
  }[tool];
  const hintText = calib ? (calib.pts.length < 2 ? `Scale photo: tap point ${calib.pts.length + 1} of 2 on a known dimension` : "Type the real distance and press Enter") : traceMove ? "Drag the photo into position, set its width, then turn Move off" : suggest?.length && tool === "draw" ? "Tap each suggested corner in order (they snap exactly) · tap the first one again to close" : hint;

  return (
    <div data-testid="sketch-mode" className="flex h-full min-h-[560px] flex-col overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-zinc-800 bg-zinc-900 px-3 py-2">
        <span className="flex items-center gap-1.5 rounded-md bg-amber-500/15 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-200">
          <Sparkle size={12} weight="fill" /> Sketch 2.0
        </span>
        <div className="flex overflow-hidden rounded-lg border border-zinc-700" data-testid="sketch-tools">
          {TOOLS.map(([k, Icon, label, key]) => (
            <button
              key={k}
              type="button"
              data-testid={`sketch-tool-${k}`}
              title={`${label} (${key})`}
              onClick={() => setTool(k)}
              className={`flex ${touch ? "h-11 px-4 text-[11px]" : "h-8 px-2.5 text-[10px]"} items-center gap-1.5 font-bold uppercase tracking-widest transition-colors ${tool === k ? "bg-amber-500 text-zinc-950" : "text-zinc-300 hover:bg-zinc-800"}`}
            >
              <Icon size={14} weight="bold" /> <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
        <div className="flex overflow-hidden rounded-lg border border-zinc-700" data-testid="sketch-ortho">
          {[
            [90, "90°"],
            [45, "45°"],
            [0, "Any"],
          ].map(([v, l]) => (
            <button key={v} type="button" data-testid={`sketch-ortho-${v}`} onClick={() => setOrtho(v)} className={`h-8 px-2 text-[10px] font-bold uppercase tracking-widest ${ortho === v ? "bg-zinc-200 text-zinc-900" : "text-zinc-400 hover:bg-zinc-800"}`}>
              {l}
            </button>
          ))}
        </div>
        <select data-testid="sketch-snap" value={snap} onChange={(e) => setSnap(Number(e.target.value))} className="h-8 rounded-lg border border-zinc-700 bg-zinc-950 px-2 text-[10px] font-bold uppercase tracking-widest text-zinc-300">
          {[0.25, 0.5, 1, 2, 3, 6].map((s) => (
            <option key={s} value={s}>
              Snap {s}"
            </option>
          ))}
        </select>
        <button type="button" data-testid="sketch-toggle-dims" onClick={() => setShowDims((d) => !d)} className="flex h-8 items-center gap-1 rounded-lg border border-zinc-700 px-2 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800">
          {showDims ? <Eye size={13} /> : <EyeSlash size={13} />} Dims
        </button>
        <button type="button" data-testid="sketch-toggle-touch" title="Tablet mode — big tap targets and an on-screen number pad" onClick={toggleTouch} className={`flex h-8 items-center gap-1 rounded-lg border px-2 text-[10px] font-bold uppercase tracking-widest ${touch ? "border-amber-400 bg-amber-500/15 text-amber-200" : "border-zinc-700 text-zinc-300 hover:bg-zinc-800"}`}>
          <DeviceTablet size={13} /> Tablet
        </button>
        {onUploadTrace && (
          <div className="flex items-center gap-1 rounded-lg border border-zinc-700 px-1" data-testid="sketch-photo-tools">
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={uploadTrace} data-testid="sketch-photo-input" />
            <button type="button" data-testid="sketch-photo-upload" title="Put a photo of the field sketch under the grid" onClick={() => fileRef.current?.click()} className="flex h-8 items-center gap-1 px-2 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800">
              <Camera size={13} /> {trace ? "Replace" : "Photo"}
            </button>
            {trace && (
              <>
                <button type="button" data-testid="sketch-photo-move" onClick={() => { setTraceMove((m) => !m); setCalib(null); }} className={`h-8 px-2 text-[10px] font-bold uppercase tracking-widest ${traceMove ? "bg-amber-500 text-zinc-950" : "text-zinc-300 hover:bg-zinc-800"}`}>
                  Move
                </button>
                <button type="button" data-testid="sketch-photo-scale" title="Tap two points on the photo, then type the real distance" onClick={() => { setCalib(calib ? null : { pts: [], value: "" }); setTraceMove(false); }} className={`h-8 px-2 text-[10px] font-bold uppercase tracking-widest ${calib ? "bg-amber-500 text-zinc-950" : "text-zinc-300 hover:bg-zinc-800"}`}>
                  Scale
                </button>
                <button type="button" data-testid="sketch-photo-detect" disabled={detecting} title="Find corners on the photo so each one is a single tap" onClick={detect} className={`flex h-8 items-center gap-1 px-2 text-[10px] font-bold uppercase tracking-widest ${suggest ? "bg-amber-500 text-zinc-950" : "text-zinc-300 hover:bg-zinc-800"} disabled:opacity-50`}>
                  <MagicWand size={12} weight="bold" /> {detecting ? "Finding…" : suggest ? `${suggest.length} corners` : "Corners"}
                </button>
                <button type="button" data-testid="sketch-photo-autotrace" disabled={detecting} title="Join the photo's outline into a piece in one tap" onClick={autoTrace} className="flex h-8 items-center gap-1 px-2 text-[10px] font-bold uppercase tracking-widest text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50">
                  <Lightning size={12} weight="fill" /> Trace it for me
                </button>
                <label className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500">
                  W
                  <input
                    data-testid="sketch-photo-width"
                    type="number"
                    value={trace.w_in || 120}
                    onChange={(e) => onTraceChange?.({ ...trace, w_in: Math.max(12, Number(e.target.value) || 12) })}
                    className="h-7 w-16 rounded border border-zinc-700 bg-zinc-950 px-1 font-mono-data text-xs text-zinc-100"
                  />
                  in
                </label>
                <input data-testid="sketch-photo-opacity" type="range" min={0.1} max={1} step={0.05} value={trace.opacity ?? 0.55} onChange={(e) => onTraceChange?.({ ...trace, opacity: Number(e.target.value) })} className="w-16 accent-amber-400" title="Photo opacity" />
                <button type="button" data-testid="sketch-photo-remove" onClick={() => { onTraceChange?.(null); setTraceMove(false); }} className="h-8 px-2 text-[10px] font-bold uppercase tracking-widest text-rose-300 hover:bg-zinc-800">
                  <X size={12} weight="bold" />
                </button>
              </>
            )}
          </div>
        )}
        <button type="button" data-testid="sketch-undo" onClick={undo} className="flex h-8 items-center gap-1 rounded-lg border border-zinc-700 px-2 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800">
          <ArrowCounterClockwise size={13} /> Undo
        </button>
        <div className="flex overflow-hidden rounded-lg border border-zinc-700">
          <button type="button" onClick={() => zoom(0.8)} className="h-8 px-2 text-xs text-zinc-300 hover:bg-zinc-800">+</button>
          <button type="button" onClick={() => zoom(1.25)} className="h-8 px-2 text-xs text-zinc-300 hover:bg-zinc-800">−</button>
          <button type="button" data-testid="sketch-fit" onClick={fit} className="h-8 px-2 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800">Fit</button>
        </div>
        <span className="ml-auto hidden items-center gap-1 text-[10px] text-zinc-500 md:flex">
          <Keyboard size={13} /> V D S C · O ortho · Shift-drag pan · ⌘Z
        </span>
        {onExit && (
          <button type="button" data-testid="sketch-exit" onClick={onExit} className="h-8 rounded-lg border border-zinc-700 px-2.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800">
            Classic studio
          </button>
        )}
      </div>

      <div className="flex min-h-0 flex-1">
        <div ref={wrapRef} className="relative min-w-0 flex-1" onWheel={(e) => zoom(e.deltaY > 0 ? 1.1 : 0.9)}>
          <SketchCanvas
            pieces={list}
            view={view}
            snap={snap}
            sel={sel}
            hover={hover}
            draw={draw}
            seamPreview={seamPreview}
            tool={tool}
            showDims={showDims}
            touch={touch}
            trace={trace && traceUrl ? { ...trace, url: traceUrl, moving: traceMove } : null}
            calib={calib}
            suggest={suggest}
            onPointer={onPointer}
            onEdgeClick={onEdgeClick}
            onCornerClick={onCornerClick}
            onCutoutPointer={onCutoutPointer}
            onDimClick={onDimClick}
            onPieceClick={onPieceClick}
          />
          {dimEdit && (
            <input
              autoFocus={!touch}
              inputMode={touch ? "none" : "decimal"}
              data-testid="sketch-dim-input"
              type="text"
              value={dimEdit.value ?? ""}
              placeholder={String(list.find((p) => p.id === dimEdit.pieceId)?.edges?.[dimEdit.edge]?.length_in ?? "")}
              onChange={(e) => setDimEdit({ ...dimEdit, value: e.target.value.replace(/[^0-9.]/g, "") })}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitDim(e.currentTarget.value);
                if (e.key === "Escape") setDimEdit(null);
                e.stopPropagation();
              }}
              onBlur={(e) => !touch && commitDim(e.currentTarget.value)}
              style={{ left: `${dimEdit.left}%`, top: `${dimEdit.top}%` }}
              className={`absolute z-10 -translate-x-1/2 -translate-y-1/2 rounded-md border-2 border-amber-400 bg-zinc-950 px-2 text-center font-mono-data font-bold text-zinc-50 shadow-xl ${touch ? "h-12 w-32 text-xl" : "h-8 w-24 text-sm"}`}
            />
          )}
          {calib?.pts?.length === 2 && (
            <div
              data-testid="sketch-calib-prompt"
              className="absolute z-10 flex -translate-x-1/2 items-center gap-2 rounded-lg border-2 border-amber-400 bg-zinc-950 p-2 shadow-xl"
              style={{ left: `${(((calib.pts[0][0] + calib.pts[1][0]) / 2 - view.x) / view.w) * 100}%`, top: `${(((calib.pts[0][1] + calib.pts[1][1]) / 2 - view.y) / view.h) * 100}%` }}
            >
              <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Real distance</span>
              <input
                autoFocus={!touch}
                inputMode={touch ? "none" : "decimal"}
                data-testid="sketch-calib-input"
                type="text"
                value={calib.value || ""}
                onChange={(e) => setCalib({ ...calib, value: e.target.value.replace(/[^0-9.]/g, "") })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") applyCalib(e.currentTarget.value);
                  if (e.key === "Escape") setCalib(null);
                  e.stopPropagation();
                }}
                className={`rounded-md border border-zinc-700 bg-zinc-900 px-2 text-center font-mono-data font-bold text-zinc-50 ${touch ? "h-11 w-28 text-xl" : "h-8 w-20 text-sm"}`}
              />
              <span className="text-xs text-zinc-400">in</span>
              <button type="button" data-testid="sketch-calib-apply" onClick={() => applyCalib(calib.value)} className="h-8 rounded-md bg-amber-500 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-950">
                Apply
              </button>
            </div>
          )}
          {touch && (
            <NumPad
              onKey={padKey}
              value={calib?.pts?.length === 2 ? calib.value : dimEdit ? dimEdit.value : draw?.typed}
              mode={calib?.pts?.length === 2 || dimEdit ? "dim" : "draw"}
              rectW={draw?.rectW}
            />
          )}
          {draw?.typed && (
            <div data-testid="sketch-typed" className="pointer-events-none absolute left-3 top-3 rounded-md border border-emerald-400/50 bg-zinc-950/90 px-3 py-1.5 font-mono-data text-lg font-bold text-emerald-300">
              {draw.rectW != null ? `${draw.rectW} × ` : ""}
              {draw.typed}"
            </div>
          )}
          <div data-testid="sketch-hint" className="pointer-events-none absolute bottom-2 left-3 right-3 truncate text-[11px] text-zinc-400">
            {hintText}
          </div>
        </div>
        <div className="w-[300px] shrink-0 border-l border-zinc-800">
          <SketchPanel
            piece={selPiece}
            sel={sel}
            edgeProfiles={edgeProfiles}
            cutoutKinds={cutoutKinds}
            products={products}
            services={services}
            areas={areas}
            currentAreaId={currentAreaId}
            onSelectArea={onSelectArea}
            onEdge={setEdge}
            onEdgeAll={setEdgeAll}
            onCorner={applyCorner}
            onCutout={setCut}
            onDeleteCutout={() => {
              update(selPiece.id, { cutouts: selPiece.cutouts.filter((c) => c.id !== sel.cutout) });
              setSel({ pieceId: selPiece.id });
            }}
            onDeletePiece={() => {
              push(list.filter((p) => p.id !== selPiece.id));
              setSel(null);
            }}
            onMovePiece={(areaId) => {
              onMovePiece?.(selPiece.id, areaId);
              setSel(null);
            }}
            onRename={(label) => update(selPiece.id, { label })}
            onAddProduct={addProduct}
            onApplyService={applyService}
            cutoutKind={cutoutKind}
            touch={touch}
            cornerFeature={selCornerFeature ? { ...selCornerFeature, ...selCornerFeature.record } : null}
            onCutoutKind={(k) => {
              setCutoutKind(k);
              setToolRaw("cutout");
              setDraw(null);
            }}
          />
        </div>
      </div>

      <div data-testid="sketch-status" className="flex shrink-0 flex-wrap items-center gap-x-5 gap-y-1 border-t border-zinc-800 bg-zinc-900 px-3 py-2 text-[11px] text-zinc-400">
        <span className="font-bold uppercase tracking-widest text-zinc-500">{areaName || "Area"}</span>
        <span>
          Pieces <b className="font-mono-data text-zinc-100" data-testid="sketch-status-pieces">{totals.pieces}</b>
        </span>
        <span>
          <b className="font-mono-data text-zinc-100" data-testid="sketch-status-sqft">{totals.sqft.toFixed(2)}</b> ft²
        </span>
        <span>
          Finished edge <b className="font-mono-data text-zinc-100">{totals.lf.toFixed(1)}</b> lf
        </span>
        <span>
          Cut-outs <b className="font-mono-data text-zinc-100">{totals.cutouts}</b>
        </span>
        {liveTotal != null && (
          <span className="ml-auto rounded-md bg-amber-500/10 px-2 py-0.5 font-mono-data text-sm font-bold text-amber-200" data-testid="sketch-status-price">
            {money(liveTotal)}
          </span>
        )}
      </div>
    </div>
  );
};
